import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'

import { fetchLibraryCatalogState, type LibraryCatalogState } from '../api/referenceImages'
import {
  resolveGalleryRemoteIngestUrl,
  uploadGalleryImageFromRemoteUrlToCloud,
  uploadGalleryImageToCloud,
} from '../api/referenceUpload'
import { useToast } from '../context/ToastContext'
import { showApiErrorToast } from '../lib/apiErrorToast'
import { formatGalleryDateTime } from '../lib/dateDisplay'
import { buildLibrarySidebarTreeModel, type LibrarySidebarTreeModel } from '../lib/folderTree'
import { libraryGallerySaveFolderPath } from '../lib/libraryPaths'
import { createLogger } from '../lib/logger'
import { fetchRemoteImageBlob,imageBlobToFile } from '../lib/remoteImageBlob'
import type { GenItem } from '../types/timeline'
import {
  chromeSelectableMediaCardClass,
  chromeSelectableMediaCornerBadgeClass,
} from './ChromeSurface'
import { LibraryFolderPathBreadcrumb } from './library/LibraryFolderPathBreadcrumb'
import { LibraryFolderTreeView } from './library/LibraryFolderTreeView'
import { ImageLightbox, type ImageLightboxItem } from './ImageLightbox'
import { BlockingModal } from './modal'
import { ThemedImage } from './ThemedImage'

const saveToLibraryLogger = createLogger('save-to-library')

export type SaveToLibraryModalFooterState = {
  busy: boolean
  primaryLabel: string
  primaryDisabled: boolean
}

type Props = {
  items: GenItem[]
  /** 该轮对话选用的参考图 URL（与 items 同源轮次），可选随主图一起存入图库 */
  referenceImageUrls?: string[]
  selectable?: boolean
  angular?: boolean
  onSaved: () => void
  /** 供 ThemeModal footer 同步主按钮文案与禁用态（标题栏同级、不参与 body 滚动） */
  onModalFooterState?: (state: SaveToLibraryModalFooterState) => void
  /** 与 useLibraryCatalog 同步：目录树 + 已入库 genItemId（与 GenItem.id 对应） */
  libraryCatalog?: LibraryCatalogState | null
  libraryCatalogLoading?: boolean
  onRefreshLibraryCatalog?: () => void | Promise<void>
}

/** 参考图存入图库的固定英文文件夹（objectKey 避免中文编码问题） */
const SAVED_REFERENCES_FOLDER = 'saved_references'

export type SaveToLibraryFormHandle = {
  submit: () => void
}

export const SaveToLibraryForm = forwardRef<SaveToLibraryFormHandle, Props>(function SaveToLibraryForm(
  {
    items,
    referenceImageUrls,
    selectable = false,
    angular = false,
    onSaved,
    onModalFooterState,
    libraryCatalog: libraryCatalogProp,
    libraryCatalogLoading: libraryCatalogLoadingProp = false,
    onRefreshLibraryCatalog,
  },
  ref,
) {
  const { showToast } = useToast()
  const batchMode = items.length > 1
  const fieldRound = angular ? 'rounded-none' : 'rounded-lg'
  const innerRound = angular ? 'rounded-none' : 'rounded-xl'

  const hasReferenceImages = referenceImageUrls != null && referenceImageUrls.length > 0
  const [saveReferenceImages, setSaveReferenceImages] = useState(false)
  const [refLightboxIndex, setRefLightboxIndex] = useState<number | null>(null)

  const defaultLabel = useMemo(
    () => items[0]?.prompt.trim().slice(0, 48) || `生成_${items[0]?.id.slice(0, 8) ?? Date.now()}`,
    [items],
  )
  const [name, setName] = useState(defaultLabel)
  const [treeModel, setTreeModel] = useState<LibrarySidebarTreeModel>(() => buildLibrarySidebarTreeModel([], []))
  const [selectedPath, setSelectedPath] = useState('')
  const [selectedIds, setSelectedIds] = useState<string[]>(() => items.map((it) => it.id))
  const [treeLoading, setTreeLoading] = useState(false)
  const [busy, setBusy] = useState(false)
  const busyRef = useRef(false)
  const [savedGenItemIds, setSavedGenItemIds] = useState<Set<string>>(() => new Set())

  const useExternalCatalog = libraryCatalogProp !== undefined

  useEffect(() => {
    setSelectedIds(items.filter((it) => !savedGenItemIds.has(it.id)).map((it) => it.id))
  }, [items, savedGenItemIds])

  useEffect(() => {
    if (useExternalCatalog) {
      if (libraryCatalogProp == null) {
        setTreeLoading(!!libraryCatalogLoadingProp)
        if (!libraryCatalogLoadingProp) {
          setTreeModel(buildLibrarySidebarTreeModel([], []))
          setSavedGenItemIds(new Set())
        }
        return
      }
      if (libraryCatalogLoadingProp) {
        setTreeLoading(true)
        return
      }
      setTreeLoading(false)
      setTreeModel(
        buildLibrarySidebarTreeModel(libraryCatalogProp.items, libraryCatalogProp.folderPaths ?? []),
      )
      setSavedGenItemIds(new Set(libraryCatalogProp.genItemIds))
      return
    }

    let alive = true
    ;(async () => {
      setTreeLoading(true)
      try {
        const catalog = await fetchLibraryCatalogState()
        if (!alive) return
        setTreeModel(buildLibrarySidebarTreeModel(catalog.items, catalog.folderPaths))
        setSavedGenItemIds(new Set(catalog.genItemIds))
      } catch (e) {
        if (!alive) return
        showApiErrorToast(showToast, e, '图库目录加载失败', 'warning')
        setTreeModel(buildLibrarySidebarTreeModel([], []))
        setSavedGenItemIds(new Set())
      } finally {
        if (alive) setTreeLoading(false)
      }
    })()
    return () => {
      alive = false
    }
  }, [
    libraryCatalogProp,
    libraryCatalogLoadingProp,
    showToast,
    useExternalCatalog,
  ])

  /** 存入图库：业务路径；根为空 → 后端走图库（七牛）空间，不占「上传的参考图」磁盘子树 */
  const folderPathForUpload = useMemo(() => libraryGallerySaveFolderPath(selectedPath), [selectedPath])

  const onSave = useCallback(async () => {
    if (busyRef.current) return
    busyRef.current = true
    const sourceItems = selectable
      ? items.filter((it) => selectedIds.includes(it.id))
      : items
    if (sourceItems.length === 0) {
      busyRef.current = false
      showToast({
        message: '请先勾选至少一张图片',
        variant: 'error',
      })
      return
    }
    const buildUploadName = (item: GenItem, index: number) => {
      const seed = name.trim().slice(0, 120) || defaultLabel
      if (!batchMode) return seed
      const serial = String(index + 1).padStart(2, '0')
      const fallback = item.prompt.trim().slice(0, 24)
      return `${seed}_${serial}${fallback ? `_${fallback}` : ''}`
    }
    setBusy(true)
    try {
      // 先存主图
      for (let i = 0; i < sourceItems.length; i++) {
        const item = sourceItems[i]
        const meta = {
          prompt: item.prompt,
          roundId: item.roundId,
          genItemId: item.id,
          modelLabel: item.modelLabel,
          aspectLabel: item.aspectLabel,
          sourceCreatedAt: formatGalleryDateTime(item.at),
          // 主图 metadata 里也记录本轮参考图（供反向追溯）
          referenceImageUrls: referenceImageUrls?.length ? referenceImageUrls : undefined,
        }
        const nameStem = buildUploadName(item, i)
        try {
          const blob = await fetchRemoteImageBlob(item.src)
          const file = await imageBlobToFile(blob, nameStem)
          await uploadGalleryImageToCloud(file, folderPathForUpload, meta)
        } catch (err) {
          saveToLibraryLogger.warn('浏览器拉取失败，改由服务端导入', err)
          const absoluteUrl = resolveGalleryRemoteIngestUrl(item.src)
          await uploadGalleryImageFromRemoteUrlToCloud(
            absoluteUrl,
            nameStem,
            folderPathForUpload,
            meta,
          )
        }
      }

      // 如果勾了，把参考图也存到图库 saved_references/ 文件夹
      if (saveReferenceImages && hasReferenceImages) {
        for (let i = 0; i < referenceImageUrls.length; i++) {
          const refUrl = referenceImageUrls[i]
          const refName = `ref_${String(i + 1).padStart(2, '0')}`
          try {
            const absoluteUrl = resolveGalleryRemoteIngestUrl(refUrl)
            await uploadGalleryImageFromRemoteUrlToCloud(
              absoluteUrl,
              refName,
              SAVED_REFERENCES_FOLDER,
              {
                prompt: `Saved as reference for round ${sourceItems[0]?.roundId ?? ''}`,
                roundId: sourceItems[0]?.roundId,
                genItemId: sourceItems[0]?.id,
                sourceCreatedAt: formatGalleryDateTime(sourceItems[0]?.at ?? new Date()),
              },
            )
          } catch (refErr) {
            saveToLibraryLogger.warn('参考图存入图库失败（继续其他）', refErr, refUrl)
          }
        }
      }

      showToast({
        message: sourceItems.length > 1 ? `已保存 ${sourceItems.length} 张到图库` : '已保存到图库',
        variant: 'success',
      })
      setSavedGenItemIds((prev) => {
        const next = new Set(prev)
        for (const it of sourceItems) next.add(it.id)
        return next
      })
      try {
        await onRefreshLibraryCatalog?.()
      } catch (refreshError) {
        saveToLibraryLogger.warn('保存后刷新目录失败', refreshError)
        showApiErrorToast(showToast, refreshError, '已保存到图库，但刷新目录失败', 'warning')
      }
      onSaved()
    } catch (e) {
      showApiErrorToast(showToast, e, '保存到图库失败', 'error')
    } finally {
      busyRef.current = false
      setBusy(false)
    }
  }, [
    batchMode,
    defaultLabel,
    folderPathForUpload,
    items,
    name,
    onSaved,
    selectable,
    referenceImageUrls,
    saveReferenceImages,
    hasReferenceImages,
    selectedIds,
    showToast,
    onRefreshLibraryCatalog,
  ])

  useImperativeHandle(ref, () => ({ submit: () => void onSave() }), [onSave])

  const targetItems = useMemo(
    () => (selectable ? items.filter((it) => selectedIds.includes(it.id)) : items),
    [items, selectable, selectedIds],
  )
  const allTargetsAlreadySaved =
    targetItems.length > 0 && targetItems.every((it) => savedGenItemIds.has(it.id))

  const primaryLabel = useMemo(
    () =>
      busy
        ? '存入中…'
        : allTargetsAlreadySaved
          ? '已在图库'
          : batchMode || selectable
            ? `批量存入 (${selectable ? selectedIds.length : items.length})`
            : '存入图库',
    [allTargetsAlreadySaved, batchMode, busy, items.length, selectable, selectedIds.length],
  )

  const primaryDisabled =
    (selectable && selectedIds.length === 0) || allTargetsAlreadySaved

  useEffect(() => {
    onModalFooterState?.({ busy, primaryLabel, primaryDisabled })
  }, [busy, onModalFooterState, primaryDisabled, primaryLabel])

  return (
    <>
      <div
        className={[
          'space-y-4 border border-line bg-panel p-4 text-left text-ink sm:p-5',
          innerRound,
        ].join(' ')}
      >
      {selectable ? (
        <div className="rounded-xl border border-line bg-surface-muted/30 p-3 sm:p-4">
          <div className="mb-2 flex items-center justify-between text-sm">
            <p className="font-semibold text-ink">当前对话图片</p>
            <p className="text-xs text-ink-soft">已选 {selectedIds.length} / {items.length}</p>
          </div>
          <div className="scroll-region max-h-52 overflow-y-auto pe-2 sm:pe-3">
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {items.map((it, idx) => {
                const active = selectedIds.includes(it.id)
                const alreadySaved = savedGenItemIds.has(it.id)
                return (
                  <label
                    key={it.id}
                    className={[
                      chromeSelectableMediaCardClass(angular, active && !alreadySaved),
                      alreadySaved ? 'cursor-not-allowed opacity-75' : 'cursor-pointer',
                    ].join(' ')}
                  >
                    <input
                      type="checkbox"
                      className="sr-only"
                      disabled={alreadySaved || busy}
                      checked={active}
                      onChange={(e) => {
                        if (alreadySaved) return
                        const checked = e.target.checked
                        setSelectedIds((prev) => {
                          if (checked) return [...prev, it.id]
                          return prev.filter((id) => id !== it.id)
                        })
                      }}
                    />
                    <ThemedImage
                      src={it.src}
                      alt=""
                      compact
                      interactiveSave
                      downloadNameHint={`save_pick_${idx + 1}`}
                      className="h-20 w-full object-cover"
                    />
                    <span className={chromeSelectableMediaCornerBadgeClass(angular, 'index')}>
                      {idx + 1}
                    </span>
                    <span
                      className={chromeSelectableMediaCornerBadgeClass(
                        angular,
                        alreadySaved ? 'saved' : active ? 'selected' : 'idle',
                      )}
                    >
                      {alreadySaved ? '已在图库' : active ? '已选' : '选择'}
                    </span>
                  </label>
                )
              })}
            </div>
          </div>
        </div>
      ) : null}
      <label className="block text-sm font-semibold text-ink">
        {batchMode ? '名称前缀' : '名称'}
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={busy}
          className={[
            'mt-2 min-h-[2.75rem] w-full border border-line bg-[var(--menu-solid)] px-3 py-2 text-base text-ink outline-none ring-2 ring-transparent placeholder:text-ink-soft focus:border-accent focus:ring-accent/25 disabled:opacity-60',
            fieldRound,
          ].join(' ')}
        />
      </label>
      {hasReferenceImages ? (
        <label className="flex cursor-pointer items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={saveReferenceImages}
            disabled={busy}
            onChange={(e) => setSaveReferenceImages(e.target.checked)}
            className="h-4 w-4 accent-accent"
          />
          <span className="font-medium text-ink">同时存入</span>
          <button
            type="button"
            onClick={() => setRefLightboxIndex(0)}
            className="font-medium text-accent underline underline-offset-2 hover:text-accent/80"
          >
            参考图
          </button>
        </label>
      ) : null}
      <div className="rounded-xl border border-line bg-surface-muted/40 p-3 sm:p-4">
        <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-3">
          <div className="min-w-0 flex-1 space-y-1.5">
            <p className="text-sm font-semibold text-ink">目标目录</p>
            <LibraryFolderPathBreadcrumb selectedFolderPath={selectedPath} onNavigate={setSelectedPath} />
          </div>
          <span className="shrink-0 text-xs text-ink-soft sm:pt-0.5">
            {treeLoading ? '读取目录中…' : '在下方选择或使用面包屑跳转'}
          </span>
        </div>
        <div
          className={[
            'scroll-region max-h-52 min-h-[7.5rem] overflow-y-auto border border-line bg-panel p-2 pe-3 sm:pe-3.5',
            fieldRound,
          ].join(' ')}
        >
          <LibraryFolderTreeView
            model={treeModel}
            selectedPath={selectedPath}
            onSelect={setSelectedPath}
            angular={angular}
            showLibraryRootRow
          />
        </div>
      </div>
      </div>
      <ImageLightbox
        open={refLightboxIndex != null}
        items={(referenceImageUrls ?? []).map<ImageLightboxItem>((src, i) => ({
          src,
          alt: `参考图 ${i + 1}`,
          subtitle: `参考图 ${i + 1}`,
        }))}
        index={refLightboxIndex ?? 0}
        onIndexChange={setRefLightboxIndex}
        angular={angular}
        onClose={() => setRefLightboxIndex(null)}
      />
      <BlockingModal open={busy} title="保存到图库" message="正在保存…" />
    </>
  )
})
